/**
 * LocationRequiredGate
 * ────────────────────
 * Mobile full-screen takeover that blocks the Tech tab when the
 * technician hasn't granted location permission. Required by policy
 * — without GPS we can't validate at_site events, compute drive time,
 * or surface on-job peer pins to dispatch.
 *
 * Two states:
 *   • "prompt"   — user hasn't been asked yet. Show CTA to enable.
 *   • "denied"   — user previously denied. Browser-specific
 *                  instructions for re-enabling.
 *
 * If the permission flips to "granted" via the device's settings, the
 * page is auto-unmounted by the parent's `onChange` listener.
 */
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { MapPin, AlertTriangle, RefreshCw } from "lucide-react";
import { requestPermission } from "@/lib/locationPermission";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}
function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent);
}

export default function LocationRequiredGate({ state, onGranted }) {
  const [working, setWorking] = useState(false);

  const onEnable = async () => {
    setWorking(true);
    try {
      const result = await requestPermission();
      if (result === "granted") onGranted?.();
    } finally {
      setWorking(false);
    }
  };

  const denied = state === "denied";

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6 bg-slate-50" data-testid="location-required-gate">
      <div className="max-w-sm w-full text-center space-y-5">
        <div className="w-14 h-14 mx-auto bg-violet-100 rounded-full flex items-center justify-center">
          <MapPin className="w-7 h-7 text-violet-700" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Location Access Required</h2>
          <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">
            ServiceOps tracks your location while you're clocked in so dispatch can see where you are and your drive time gets logged correctly.
          </p>
        </div>

        {!denied ? (
          <Button
            onClick={onEnable}
            disabled={working}
            className="w-full bg-violet-700 hover:bg-violet-800"
            data-testid="btn-enable-location"
          >
            {working ? "Requesting…" : "Enable Location"}
          </Button>
        ) : (
          <div className="space-y-3 text-left">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="location-denied-help">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-900 space-y-1.5">
                <div className="font-semibold">Location is blocked.</div>
                {isIOS() ? (
                  <ol className="list-decimal ml-4 space-y-1">
                    <li>Open <strong>Settings</strong> → <strong>Safari</strong> → <strong>Location</strong></li>
                    <li>Set to <strong>Ask</strong> or <strong>Allow</strong></li>
                    <li>Return here and refresh</li>
                  </ol>
                ) : isAndroid() ? (
                  <ol className="list-decimal ml-4 space-y-1">
                    <li>Tap the lock icon left of the URL bar</li>
                    <li>Tap <strong>Permissions</strong> → <strong>Location</strong></li>
                    <li>Choose <strong>Allow</strong>, then refresh</li>
                  </ol>
                ) : (
                  <ol className="list-decimal ml-4 space-y-1">
                    <li>Click the location icon in the address bar</li>
                    <li>Choose <strong>Always allow</strong> for this site</li>
                    <li>Refresh the page</li>
                  </ol>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
              className="w-full"
              data-testid="btn-refresh-after-enable"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> I've enabled it — Refresh
            </Button>
          </div>
        )}

        <div className="text-[11px] text-slate-400 pt-2 border-t border-slate-200">
          GPS works <strong>without cell signal</strong> — your phone reads satellites directly. Pings save locally and sync when you have signal again.
        </div>
      </div>
    </div>
  );
}
